import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useStoreByName } from '../../../hooks/use-stores.hooks';
import { useTranslation } from 'react-i18next';
import { useAdminUsers } from '../../../hooks/useAdminQuery';
import { useQueryClient } from '@tanstack/react-query';
import {
  Button,
  TextField,
  IconButton,
  Chip,
  CircularProgress,
  InputAdornment,
  TableSortLabel,
  useMediaQuery,
  useTheme,
  SelectChangeEvent,
  Dialog,
  DialogTitle,
  DialogActions,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Class as ClassIcon,
  Business as BusinessIcon,
  CloudUpload as CloudUploadIcon,
} from '@mui/icons-material';
import { getSchoolsAndGrades, createSchool, createGrade, getUsers } from '../../../services/api/admin.service';
import { addStudentsToClass } from '../../../services/api/teacher.service';
import axiosInstance from '../../../services/api/axios-instance';
import { ROLES, SchoolActivity, AddStudentsToClassRequest } from '@shared/types';
import UserFormModal, { UserFormValues } from '../../components/modals/UserFormModal';
import SchoolFormModal from '../../components/modals/SchoolFormModal';
import GradeFormModal from '../../components/modals/GradeFormModal';
import DeleteConfirmationModal from '../../components/modals/DeleteConfirmationModal';
import MessageModal from '../../components/modals/MessageModal';
import ForeignKeyErrorModal from '../../components/modals/ForeignKeyErrorModal';
import ImportStudentsModal from '../../components/modals/ImportStudentsModal';
import ImportExcelModal from '../../components/modals/ImportExcelModal';
import FailedUsersModal from '../../components/modals/FailedUsersModal';
import { FailedUser } from '@shared/types';

// סוג עבור משתמש
interface User {
  id: string | number;
  name: string;
  email: string;
  role_id?: number;
  role?: { id?: number; name?: string };
  school?: { id?: number; name?: string };
  grade_ids?: number[];
  created_at: string | number | Date;
}

// סוג עבור כיוון מיון
type Order = 'asc' | 'desc';

/**
 * דף ניהול משתמשים
 * מאפשר צפייה, הוספה, עריכה ומחיקה של משתמשים
 */
const UsersManagementPage = observer(() => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const adminStore = useStoreByName('adminStore');
  const userStore = useStoreByName('userStore');
  const queryClient = useQueryClient();

  // Modern TanStack Query data fetching
  const { data: usersData, isLoading } = useAdminUsers(adminStore.selectedSchoolId);
  const users = usersData?.users || [];

  // בדיקת סוג המשתמש
  const userRoleId = userStore.userData?.personalInfo?.role?.id;
  const isAdmin = userRoleId === ROLES.ADMIN;
  const isPrincipal = userRoleId === ROLES.PRINCIPAL;

  // משתני סטייט לחיפוש וסינון
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredUsers, setFilteredUsers] = useState<User[]>([]);

  // משתני סטייט למיון
  const [orderBy, setOrderBy] = useState<string>('name');
  const [order, setOrder] = useState<Order>('asc');

  // משתני סטייט לטפסים
  const [openDialog, setOpenDialog] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [isSaving, setIsSaving] = useState(false); // loader עבור שמירה
  const [isDeleting, setIsDeleting] = useState(false); // loader עבור מחיקה
  const [formData, setFormData] = useState<UserFormValues>({
    name: '',
    email: '',
    password: '',
    role_id: 2, // ברירת מחדל: תלמיד
    school_id: 0, // Single school for new modal
    grade_ids: [] as number[], // רשימת כיתות
  });

  // משתני סטייט להודעות
  const [resultDialog, setResultDialog] = useState({
    open: false,
    success: true,
    message: '',
  }); 

  // משתני סטייט לדיאלוג אישור מחיקה
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [userToDelete, setUserToDelete] = useState<User | null>(null);

  // משתני סטייט לדיאלוג אזהרת foreign key
  const [openForeignKeyDialog, setOpenForeignKeyDialog] = useState(false);
  const [foreignKeyError, setForeignKeyError] = useState<any>(null);
  const [userToForceDelete, setUserToForceDelete] = useState<User | null>(null);

  // State עבור בתי ספר וכיתות
  const [schools, setSchools] = useState<Array<{ id: number; name: string }>>([]);
  const [grades, setGrades] = useState<
    Array<{ id: number; name: string; school_id: number; teacher?: number; School: { id: number; name: string } }>
  >([]);
  const [availableGrades, setAvailableGrades] = useState<
    Array<{ id: number; name: string; school_id: number; teacher?: number; School: { id: number; name: string } }>
  >([]);

  // State למודאל משתמשים שנכשלו
  const [failedUsers, setFailedUsers] = useState<FailedUser[]>([]);
  const [showFailedUsersModal, setShowFailedUsersModal] = useState(false);

  // State לדיאלוג הוספת בית ספר
  const [openSchoolDialog, setOpenSchoolDialog] = useState(false);
  const [isSavingSchool, setIsSavingSchool] = useState(false);
  const [schoolFormData, setSchoolFormData] = useState({
    name: '',
    address: '',
  });

  // State לדיאלוג הוספת כיתה
  const [openGradeDialog, setOpenGradeDialog] = useState(false);
  const [isSavingGrade, setIsSavingGrade] = useState(false);
  const [gradeFormData, setGradeFormData] = useState({
    name: '',
    school_id: 0,
    teacher_id: 0,
  });

  // State למורים זמינים
  const [teachers, setTeachers] = useState<Array<{ id: number; name: string; email: string }>>([]);
  const [teacherSearchTerm, setTeacherSearchTerm] = useState('');

  // State לשרשור ייבוא תלמידים אחרי יצירת כיתה
  const [newlyCreatedGrade, setNewlyCreatedGrade] = useState<{ id: number; name: string; school_id: number } | null>(null);
  const [showAddStudentsPrompt, setShowAddStudentsPrompt] = useState(false);
  const [showImportExcelForNewGrade, setShowImportExcelForNewGrade] = useState(false);
  const [importFailedUsers, setImportFailedUsers] = useState<FailedUser[]>([]);
  const [showImportFailedUsersModal, setShowImportFailedUsersModal] = useState(false);

  // State לדיאלוג ייבוא תלמידים
  const [openImportDialog, setOpenImportDialog] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [importFormData, setImportFormData] = useState({
    selectedGradeId: 0, // כיתה קיימת
    createNewGrade: false, // האם ליצור כיתה חדשה
    newGradeName: '',
    newGradeSchoolId: 0,
    selectedTeacherId: 0 as number | null,
  });

  // טעינת בתי ספר וכיתות
  const loadSchoolsAndGrades = async () => {
    try {
      const data = await getSchoolsAndGrades();
      setSchools(data.schools);
      setGrades(data.grades);
    } catch (error) {
      console.error('Error loading schools and grades:', error);
      showAlert('error', t('errorLoadingSchools'));
    }
  };

  // טעינת מורים זמינים
  const loadTeachers = async () => {
    try {
      const data = await getUsers();
      // סינון רק מורים (role_id = 1) ומנהלים (role_id = 3) שיכולים לשמש כמורים
      const availableTeachers = data.users
        .filter((user: any) => user.role_id === 1 || user.role_id === 3)
        .map((user: any) => ({
          id: user.id,
          name: user.name,
          email: user.email,
        }));
      setTeachers(availableTeachers);
    } catch (error) {
      console.error('Error loading teachers:', error);
      showAlert('error', t('errorLoadingTeachers'));
    }
  };

  // פונקציה להצגת הודעות
  const showAlert = (type: 'success' | 'error' | 'warning', message: string) => {
    setResultDialog({
      open: true,
      success: type === 'success',
      message: message,
    });
  };

  // פונקציות לדיאלוג בית ספר
  const handleOpenSchoolDialog = () => {
    setSchoolFormData({ name: '', address: '' });
    setOpenSchoolDialog(true);
  };

  const handleCloseSchoolDialog = () => {
    if (!isSavingSchool) {
      setOpenSchoolDialog(false);
      setSchoolFormData({ name: '', address: '' });
    }
  };

  // Helper functions for SchoolFormModal
  const handleSchoolFormChange = (field: string, value: string) => {
    setSchoolFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSchoolFormSubmit = async (formData: { name: string; address: string }) => {
    setIsSavingSchool(true);
    try {
      await createSchool({ name: formData.name.trim() });
      setSchoolFormData({ name: '', address: '' });
      setOpenSchoolDialog(false);
      await loadSchoolsAndGrades();
      // Show success message
      setResultDialog({
        open: true,
        success: true,
        message: t('schoolCreatedSuccessfully'),
      });
    } catch (error: any) {
      console.error('Error creating school:', error);
      const msg = error.response?.data?.error || t('schoolCreationFailed');
      setResultDialog({
        open: true,
        success: false,
        message: msg,
      });
    } finally {
      setIsSavingSchool(false);
    }
  };

  // פונקציות לדיאלוג כיתה
  const handleOpenGradeDialog = async () => {
    // טעינת מורים אם עדיין לא נטענו
    if (teachers.length === 0) {
      await loadTeachers();
    }
    setGradeFormData({ name: '', school_id: 0, teacher_id: 0 });
    setOpenGradeDialog(true);
  };

  const handleCloseGradeDialog = () => {
    if (!isSavingGrade) {
      setOpenGradeDialog(false);
      setGradeFormData({ name: '', school_id: 0, teacher_id: 0 });
      setTeacherSearchTerm(''); // איפוס חיפוש המורים
    }
  };

  // Helper functions for GradeFormModal
  const handleGradeFormChange = (field: string, value: string | number) => {
    setGradeFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleGradeFormSubmit = async (formData: { name: string; school_id: number; teacher_id: number }) => {
    setIsSavingGrade(true);
    try {
      const response = await createGrade({
        name: formData.name.trim(),
        school_id: formData.school_id,
        ...(formData.teacher_id ? { teacher_id: formData.teacher_id } : {}),
      });
      setGradeFormData({ name: '', school_id: 0, teacher_id: 0 });
      setOpenGradeDialog(false);
      await loadSchoolsAndGrades();
      setNewlyCreatedGrade(response.grade);
      setShowAddStudentsPrompt(true);
    } catch (error: any) {
      console.error('Error creating grade:', error);
      const msg = error.response?.data?.error || t('classCreationFailed');
      setResultDialog({
        open: true,
        success: false,
        message: msg,
      });
    } finally {
      setIsSavingGrade(false);
    }
  };

  const handleImportToNewGrade = async (file: File) => {
    if (!newlyCreatedGrade) return;
    try {
      const studentsData: AddStudentsToClassRequest = {
        classId: newlyCreatedGrade.id,
        schoolId: newlyCreatedGrade.school_id,
        file,
        name: newlyCreatedGrade.name,
      };
      const response = await addStudentsToClass(studentsData);
      if (response.details?.failed && response.details.failed.length > 0) {
        setImportFailedUsers(response.details.failed);
        setShowImportFailedUsersModal(true);
      } else {
        setResultDialog({
          open: true,
          success: true,
          message: t('studentsImportedSuccessfully'),
        });
      }
      await loadSchoolsAndGrades();
    } catch (error) {
      console.error('Error importing students:', error);
      setResultDialog({
        open: true,
        success: false,
        message: t('studentsImportFailed'),
      });
    } finally {
      setNewlyCreatedGrade(null);
      setShowImportExcelForNewGrade(false);
    }
  };

  // פונקציות לדיאלוג ייבוא תלמידים
  const handleOpenImportDialog = async () => {
    // טעינת מורים אם עדיין לא נטענו
    if (teachers.length === 0) {
      await loadTeachers();
    }
    setImportFormData({
      selectedGradeId: 0,
      createNewGrade: false,
      newGradeName: '',
      newGradeSchoolId: 0,
      selectedTeacherId: null,
    });
    setSelectedFile(null);
    setOpenImportDialog(true);
  };

  const handleCloseImportDialog = () => {
    if (!isImporting) {
      setOpenImportDialog(false);
      setImportFormData({
        selectedGradeId: 0,
        createNewGrade: false,
        newGradeName: '',
        newGradeSchoolId: 0,
        selectedTeacherId: null,
      });
      setSelectedFile(null);
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // בדיקה שזה קובץ Excel
      const allowedTypes = [
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'application/vnd.ms-excel',
      ];
      if (allowedTypes.includes(file.type)) {
        setSelectedFile(file);
      } else {
        showAlert('error', t('pleaseSelectExcelFile'));
      }
    }
  };

  const handleImportFormChange = (field: string, value: any) => {
    setImportFormData({ ...importFormData, [field]: value });
  };

  // validation עבור ייבוא תלמידים
  const validateImportForm = () => {
    const errors: string[] = [];

    if (!selectedFile) {
      errors.push('חובה לבחור קובץ Excel לייבוא');
    }

    if (importFormData.createNewGrade) {
      // יצירת כיתה חדשה
      if (!importFormData.newGradeName.trim()) {
        errors.push('חובה להכניס שם לכיתה החדשה');
      } else if (importFormData.newGradeName.trim().length > 50) {
        errors.push(t('classNameMaxLength'));
      }

      if (!importFormData.newGradeSchoolId) {
        errors.push('חובה לבחור בית ספר עבור הכיתה החדשה');
      }

      if (importFormData.selectedTeacherId) {
        const teacherAlreadyAssigned = grades.some((grade) => grade.teacher === importFormData.selectedTeacherId);
        if (teacherAlreadyAssigned) {
          const selectedTeacher = teachers.find((t) => t.id === importFormData.selectedTeacherId);
          errors.push(`המורה ${selectedTeacher?.name || 'הנבחר'} כבר מלמד כיתה אחרת`);
        }
      }

      // בדיקה שאין כיתה עם אותו שם באותו בית ספר
      if (importFormData.newGradeName.trim() && importFormData.newGradeSchoolId) {
        const gradeExists = grades.some(
          (grade) =>
            grade.school_id === importFormData.newGradeSchoolId &&
            grade.name.toLowerCase() === importFormData.newGradeName.trim().toLowerCase(),
        );
        if (gradeExists) {
          errors.push(t('classAlreadyExists'));
        }
      }
    } else {
      // כיתה קיימת
      if (!importFormData.selectedGradeId) {
        errors.push(t('mustSelectOrCreateClass'));
      }
    }

    return errors;
  };

  const handleSubmitImport = async () => {
    const validationErrors = validateImportForm();
    if (validationErrors.length > 0) {
      showAlert('error', `נמצאו שגיאות בטופס:\n\n• ${validationErrors.join('\n• ')}`);
      return;
    }

    setIsImporting(true);
    try {
      let targetGradeId = importFormData.selectedGradeId;

      // אם צריך ליצור כיתה חדשה
      if (importFormData.createNewGrade) {
        const gradePayload: any = {
          name: importFormData.newGradeName.trim(),
          school_id: importFormData.newGradeSchoolId,
        };

        if (importFormData.selectedTeacherId) {
          gradePayload.teacher_id = importFormData.selectedTeacherId;
        }

        const newGradeResponse = await createGrade(gradePayload);
        targetGradeId = newGradeResponse.grade.id;

        // רענון נתונים אחרי יצירת הכיתה
        await loadSchoolsAndGrades();
        await loadTeachers();
      }

      // ייבוא התלמידים לכיתה
      const formData = new FormData();
      formData.append('file', selectedFile!);
      formData.append('gradeId', targetGradeId.toString());

      // שליחה לשרת (משתמש ב-endpoint הקיים)
      const response = await axiosInstance.post('/import-class-data', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      const result = response.data;

      // רענון נתונים - TanStack Query handles this automatically via invalidation
      await loadSchoolsAndGrades();

      // טיפול בכשלונות
      if (result.details?.failed && result.details.failed.length > 0) {
        setFailedUsers(result.details.failed);
        setShowFailedUsersModal(true);
      }

      // הצגת הודעה לפי status
      if (result.status === 'error') {
        showAlert('error', result.message);
      } else if (result.status === 'partial') {
        showAlert('warning', result.message);
      } else {
        showAlert('success', result.message || 'ייבוא התלמידים הושלם בהצלחה!');
      }

      handleCloseImportDialog();
    } catch (error) {
      console.error('Error importing students:', error);
      showAlert('error', getErrorMessage(error, false));
    } finally {
      setIsImporting(false);
    }
  };

  // פונקציה לvalidation צד לקוח
  const validateForm = (data?: UserFormValues) => {
    const target = data ?? formData;
    const errors: string[] = [];

    // בדיקת שם
    if (!target.name || target.name.trim().length < 2) {
      errors.push('שם המשתמש חייב להכיל לפחות 2 תווים');
    } else if (target.name.trim().length > 100) {
      errors.push('שם המשתמש חייב להיות קצר מ-100 תווים');
    }

    // בדיקת אימייל
    if (!target.email || target.email.trim() === '') {
      errors.push('נדרשת כתובת אימייל');
    } else {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(target.email.trim())) {
        errors.push('כתובת האימייל אינה תקינה (דוגמה: user@example.com)');
      } else if (target.email.trim().length > 255) {
        errors.push('כתובת האימייל ארוכה מדי (מקסימום 255 תווים)');
      }
    }

    // בדיקת תפקיד - מנהלי בתי ספר לא יכולים ליצור מנהלי מערכת
    if (!target.role_id || target.role_id < 1 || target.role_id > 4) {
      errors.push('נדרש לבחור תפקיד תקין');
    }

    // מנהל בית ספר לא יכול ליצור משתמשי מנהל מערכת או מנהלי בתי ספר אחרים
    if (isPrincipal && (target.role_id === ROLES.ADMIN || target.role_id === ROLES.PRINCIPAL)) {
      errors.push('אין לך הרשאה ליצור משתמשים עם תפקיד זה');
    }

    // בדיקת כיתות (לתלמידים)
    // role_id === 2 = תלמיד
    if (target.role_id === 2) {
      // תלמיד יכול להיות משויך לכיתה אחת בלבד (אם נבחרה כיתה)
      if (target.grade_ids && target.grade_ids.length > 1) {
        errors.push('תלמיד יכול להיות משויך לכיתה אחת בלבד');
      }
    }

    return errors;
  };

  // פונקציה לניתוח שגיאות ומתן הודעות ברורות
  const getErrorMessage = (error: any, isEditMode = false) => {
    console.error('Error details:', error);

    // בדיקה אם יש הודעת שגיאה מהשרת
    const serverMessage = error?.response?.data?.error || error?.response?.data?.message;
    const statusCode = error?.response?.status;

    // שגיאות אימייל כפול
    if (
      serverMessage?.includes('duplicate key') ||
      serverMessage?.includes('unique constraint') ||
      serverMessage?.includes('email') ||
      statusCode === 409
    ) {
      // בדיקה אם זו שגיאה של בית ספר או כיתה
      if (serverMessage?.includes('בית ספר') || serverMessage?.includes('school')) {
        return serverMessage || 'כבר קיים בית ספר עם השם הזה';
      }
      if (serverMessage?.includes('כיתה') || serverMessage?.includes('grade')) {
        return serverMessage || 'כבר קיימת כיתה עם השם הזה בבית הספר הנבחר';
      }
      return 'כתובת האימייל כבר קיימת במערכת. אנא בחר כתובת אימייל אחרת.';
    }

    // שגיאות validation
    if (statusCode === 400) {
      if (serverMessage?.includes('email')) {
        return 'כתובת האימייל אינה תקינה. אנא בדוק את הפורמט.';
      }
      if (serverMessage?.includes('name')) {
        return 'שם המשתמש חייב להכיל לפחות 2 תווים.';
      }
      if (serverMessage?.includes('role')) {
        return 'נדרש לבחור תפקיד למשתמש.';
      }
      if (serverMessage?.includes('school')) {
        return 'נדרש לבחור בית ספר תקין.';
      }
      if (serverMessage?.includes('grade')) {
        return t('invalidClassesSelected');
      }
      if (serverMessage?.includes('בית הספר') && serverMessage?.includes('לא נמצא')) {
        return t('selectedSchoolNotFound');
      }
      if (serverMessage?.includes('required')) {
        return 'נדרש למלא את כל השדות החובה.';
      }
      if (serverMessage?.includes('length') || serverMessage?.includes('too long')) {
        return t('fieldTooLong');
      }
      // הודעות שגיאה מותאמות לבתי ספר וכיתות
      if (serverMessage?.includes('שם בית הספר חייב להכיל לפחות 2 תווים')) {
        return t('schoolNameMinLength');
      }
      if (serverMessage?.includes('שם בית הספר חייב להיות קצר מ-100 תווים')) {
        return t('schoolNameMaxLength');
      }
      if (serverMessage?.includes('שם הכיתה חייב להכיל לפחות תו אחד')) {
        return t('classNameMinLength');
      }
      if (serverMessage?.includes('שם הכיתה חייב להיות קצר מ-50 תווים')) {
        return t('classNameMaxLength');
      }
      if (serverMessage?.includes('חובה לבחור בית ספר תקין עבור הכיתה')) {
        return t('mustSelectValidSchool');
      }
      if (serverMessage?.includes('חובה לבחור מורה תקין עבור הכיתה')) {
        return t('mustSelectValidTeacher');
      }
      if (serverMessage?.includes('המורה הנבחר לא נמצא במערכת')) {
        return t('teacherNotFound');
      }
      if (serverMessage?.includes('כבר מלמד את הכיתה')) {
        return serverMessage; // השרת כבר שולח הודעה מפורטת
      }
      return serverMessage || t('invalidData');
    }

    // שגיאות הרשאה
    if (statusCode === 401 || statusCode === 403) {
      return t('noPermission');
    }

    // שגיאה כשמשתמש לא נמצא
    if (statusCode === 404) {
      if (isEditMode) {
        return t('userNotFoundEdit');
      } else {
        return t('userNotFoundDelete');
      }
    }

    // שגיאות foreign key (מחיקה)
    if (
      serverMessage?.includes('foreign key') ||
      serverMessage?.includes('constraint') ||
      serverMessage?.includes('still referenced')
    ) {
      return t('cannotDeleteUser');
    }

    // שגיאת rate limiting
    if (statusCode === 429) {
      return t('tooManyRequests');
    }

    // שגיאת maintenance
    if (statusCode === 503) {
      return t('maintenanceMode');
    }

    // שגיאות שרת
    if (statusCode >= 500) {
      return t('serverError');
    }

    // שגיאות רשת
    if (error?.code === 'NETWORK_ERROR' || !statusCode) {
      return t('networkError');
    }

    // הודעת שגיאה כללית עם פרטים אם יש
    if (serverMessage) {
      return `${t('error')}: ${serverMessage}`;
    }

    // הודעה כללית
    return isEditMode ? t('updateUserError') : t('addUserError');
  };

  // עדכון כיתות זמינות לפי בית הספר הנבחר
  const updateAvailableGrades = () => {
    if (formData.school_id > 0) {
      // סינון כיתות לפי בית הספר הנבחר
      let filtered = grades.filter((grade) => grade.school_id === formData.school_id);

      // במצב עריכה, כולל גם כיתות שהמשתמש כבר מקושר אליהן
      // אפילו אם הן לא נמצאות בבית הספר הנבחר כרגע
      if (isEditMode && formData.grade_ids.length > 0) {
        const userCurrentGrades = grades.filter(
          (grade) => formData.grade_ids.includes(grade.id) && !filtered.some((fg) => fg.id === grade.id),
        );
        filtered = [...filtered, ...userCurrentGrades];
      }

      setAvailableGrades(filtered);
    } else {
      setAvailableGrades([]);
    }
  };

  // Helper function for UserFormModal submit
  const handleUserFormSubmit = async (modalFormData: UserFormValues) => {
    await handleSubmit(modalFormData);
  };

  useEffect(() => {
    // TanStack Query automatically handles data loading based on adminStore.selectedSchoolId
    // No manual loading needed - useAdminUsers hook handles this automatically
    loadSchoolsAndGrades();
    loadTeachers();
  }, [adminStore.selectedSchoolId]);

  // useEffect לטיפול בשינויים בתפקיד או בית הספר
  useEffect(() => {
    updateAvailableGrades();
  }, [formData.role_id, formData.school_id, grades]);

  // אפקט חדש לאתחול נתונים כשמשתנה ה-users
  useEffect(() => {
    if (users && users.length > 0) {
      // טיפול נכון בנתונים מהשרת - שימור הנתונים המלאים
      const typedUsers = users.map(
        (user: any): User => ({
          id: user.id,
          name: user.name || '',
          email: user.email || '',
          role_id: Number(user.role_id) || 0,
          role: user.role || {},
          school: user.school || {},
          grade_ids: user.grade_ids || [],
          created_at: user.created_at || new Date().toISOString(),
        }),
      );

      setFilteredUsers(typedUsers);
    }
  }, [users]);

  // אפקט נפרד לחיפוש ומיון
  useEffect(() => {
    // סינון המשתמשים לפי חיפוש
    if (users && users.length > 0) {
      // שימוש ישיר בנתוני המשתמשים מה-store ללא מיפוי מיותר
      const typedUsers = users.map(
        (user: any): User => ({
          id: user.id,
          name: user.name || '',
          email: user.email || '',
          role_id: Number(user.role_id) || 0,
          role: user.role || {},
          school: user.school || {},
          grade_ids: user.grade_ids || [],
          created_at: user.created_at || new Date().toISOString(),
        }),
      );

      const filtered = typedUsers.filter(
        (user: User) =>
          user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          (user.email ?? '').toLowerCase().includes(searchTerm.toLowerCase()),
      );

      // מיון המשתמשים המסוננים
      const sortedUsers = sortUsers(filtered, orderBy, order);
      setFilteredUsers(sortedUsers);
    }
  }, [users, searchTerm, orderBy, order]);

  // פונקציית מיון
  const sortUsers = (usersToSort: User[], field: string, sortOrder: Order) => {
    if (usersToSort.length === 0) return [];

    // בדיקה שהנתונים הראשונים קיימים לפי השדה
    const firstUser = usersToSort[0];

    return [...usersToSort].sort((a, b) => {
      let valueA: any, valueB: any;

      // קביעת ערכים להשוואה לפי השדה
      switch (field) {
        case 'name':
          valueA = (a.name || '').toLowerCase();
          valueB = (b.name || '').toLowerCase();
          break;
        case 'role':
          // וידוא קבלת ערך מספרי לתפקיד
          valueA = Number(a.role_id) || Number(a.role?.id) || 0;
          valueB = Number(b.role_id) || Number(b.role?.id) || 0;
          break;
        case 'created_at':
          // המרה למספר לשם השוואה נכונה של תאריכים
          valueA = new Date(a.created_at || 0).getTime();
          valueB = new Date(b.created_at || 0).getTime();
          break;
        default:
          valueA = a[field as keyof User] || '';
          valueB = b[field as keyof User] || '';
      }

      // ביצוע ההשוואה לפי סדר המיון
      if (sortOrder === 'asc') {
        return valueA > valueB ? 1 : valueA < valueB ? -1 : 0;
      } else {
        return valueA < valueB ? 1 : valueA > valueB ? -1 : 0;
      }
    });
  };

  // פונקציה לשינוי המיון
  const handleRequestSort = (field: string) => {
    const isAsc = orderBy === field && order === 'asc';
    const newOrder = isAsc ? 'desc' : 'asc';
    setOrder(newOrder);
    setOrderBy(field);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const handleOpenDialog = async (user: User | null = null) => {
    // וודא שבתי ספר וכיתות נטענו
    if (schools.length === 0 || grades.length === 0) {
      await loadSchoolsAndGrades();
    }

    if (user) {
      // עריכת משתמש קיים
      setIsEditMode(true);
      setCurrentUser(user);

      const roleId = user.role_id || user.role?.id || 2;
      let userSchoolId: number = 0;
      let userGradeIds: number[] = [];

      if (roleId === 1) {
        // מורה - כיתות שהוא מלמד (Grade.teacher = user.id)
        const teacherGrades = grades.filter((grade) => grade.teacher === user.id);
        if (teacherGrades.length > 0) {
          userSchoolId = teacherGrades[0].school_id;
          userGradeIds = teacherGrades.map((grade) => grade.id);
        } else if (user.school?.id) {
          userSchoolId = user.school.id;
        }
      } else if (roleId === 2) {
        // תלמיד - כיתות מ-grade_ids
        if (user.grade_ids && user.grade_ids.length > 0) {
          const userGrades = grades.filter((grade) => user.grade_ids!.includes(grade.id));
          if (userGrades.length > 0) {
            userSchoolId = userGrades[0].school_id;
            userGradeIds = user.grade_ids;
          }
        } else if (user.school?.id) {
          userSchoolId = user.school.id;
        }
      } else if (user.school?.id) {
        // אדמין או תפקיד אחר
        userSchoolId = user.school.id;
      }

      setFormData({
        name: user.name || '',
        email: user.email || '',
        password: '', // Password not needed for edit mode
        role_id: roleId,
        school_id: userSchoolId,
        grade_ids: userGradeIds,
      });
    } else {
      // הוספת משתמש חדש
      setIsEditMode(false);
      setCurrentUser(null);
      setFormData({
        name: '',
        email: '',
        password: '',
        role_id: 2,
        school_id: 0,
        grade_ids: [],
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    if (!isSaving) {
      // לא ניתן לסגור במהלך שמירה
      setOpenDialog(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleSelectChange = (e: SelectChangeEvent<number>) => {
    const { name, value } = e.target;

    // אם משנים תפקיד, צריך לעדכן את הסטייט בהתאם
    if (name === 'role_id') {
      const roleId = Number(value);
      setFormData({
        ...formData,
        role_id: roleId,
        grade_ids: [], // איפוס כיתות כשמשנים תפקיד
      });

      // איפוס בית ספר וכיתות אם לא מורה
      if (roleId !== 1) {
        setFormData({ ...formData, role_id: roleId, school_id: 0, grade_ids: [] });
      }
    }
  };

  const handleCloseResultDialog = () => {
    setResultDialog({ ...resultDialog, open: false });
  };

  const handleSubmit = async (modalFormData?: UserFormValues) => {
    const dataToSubmit = modalFormData ?? formData;

    // בדיקת validation לפני השליחה
    const validationErrors = validateForm(dataToSubmit);
    if (validationErrors.length > 0) {
      setResultDialog({
        open: true,
        success: false,
        message: `נמצאו שגיאות בטופס:\n\n• ${validationErrors.join('\n• ')}`,
      });
      return;
    }

    setIsSaving(true); // הצגת loader
    try {
      // הנתונים כבר בפורמט הנכון עבור השרת
      const submissionData = {
        ...dataToSubmit,
        school_id: dataToSubmit.school_id || 1,
      };

      if (isEditMode && currentUser) {
        // עדכון משתמש קיים
        await adminStore.updateExistingUser(String(currentUser.id), submissionData);

        // רענון נתונים אחרי עדכון מוצלח
        await loadSchoolsAndGrades();
        
        // רענון רשימת המשתמשים
        queryClient.invalidateQueries({ queryKey: ["admin", "users"] });

        // הצגת הודעת הצלחה בדיאלוג
        setResultDialog({
          open: true,
          success: true,
          message: 'פרטי המשתמש עודכנו בהצלחה',
        });
      } else {
        // יצירת משתמש חדש
        await adminStore.createNewUser(submissionData);

        // רענון נתונים אחרי יצירה מוצלחת
        await loadSchoolsAndGrades();
        
        // רענון רשימת המשתמשים
        queryClient.invalidateQueries({ queryKey: ["admin", "users"] });

        // הצגת הודעת הצלחה בדיאלוג
        setResultDialog({
          open: true,
          success: true,
          message: 'המשתמש נוסף בהצלחה',
        });
      }
      handleCloseDialog();
    } catch (error) {
      console.error('Error saving user:', error);
      // הצגת הודעת שגיאה מפורטת בדיאלוג
      setResultDialog({
        open: true,
        success: false,
        message: getErrorMessage(error, isEditMode),
      });
      handleCloseDialog();
    } finally {
      setIsSaving(false); // הסתרת loader
    }
  };

  const handleOpenDeleteDialog = (user: User) => {
    setUserToDelete(user);
    setOpenDeleteDialog(true);
  };

  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
    setUserToDelete(null);
  };

  const handleDeleteUser = async (forceDelete = false) => {
    if (userToDelete) {
      setIsDeleting(true);
      try {
        await adminStore.removeUser(String(userToDelete.id), forceDelete);
        handleCloseDeleteDialog();
        handleCloseForeignKeyDialog();

        // רענון נתונים אחרי מחיקה מוצלחת
        await loadSchoolsAndGrades();
        
        // רענון רשימת המשתמשים
        queryClient.invalidateQueries({ queryKey: ["admin", "users"] });

        // הצגת הודעת הצלחה בדיאלוג
        setResultDialog({
          open: true,
          success: true,
          message: forceDelete ? t('userDeletedWithDataSuccessfully') : t('userDeletedSuccessfully'),
        });
      } catch (error: any) {
        console.error('Error deleting user:', error);

        // בדיקה אם זו שגיאת foreign key constraint
        if (error.message === 'FOREIGN_KEY_CONSTRAINT') {
          setForeignKeyError(error.response?.data);
          setUserToForceDelete(userToDelete); // שמירת המשתמש לדיאלוג foreign key
          setOpenForeignKeyDialog(true);
          handleCloseDeleteDialog();
          return;
        }

        handleCloseDeleteDialog();
        handleCloseForeignKeyDialog();

        // הצגת הודעת שגיאה מפורטת בדיאלוג
        setResultDialog({
          open: true,
          success: false,
          message: getErrorMessage(error, false),
        });
      } finally {
        setIsDeleting(false);
      }
    }
  };

  const handleCloseForeignKeyDialog = () => {
    setOpenForeignKeyDialog(false);
    setForeignKeyError(null);
    setUserToForceDelete(null);
  };

  const handleConfirmForceDelete = async () => {
    if (userToForceDelete) {
      setIsDeleting(true);
      try {
        await adminStore.removeUser(String(userToForceDelete.id), true);
        handleCloseForeignKeyDialog();

        // רענון נתונים אחרי מחיקה מוצלחת
        await loadSchoolsAndGrades();
        
        // רענון רשימת המשתמשים
        queryClient.invalidateQueries({ queryKey: ["admin", "users"] });

        setResultDialog({
          open: true,
          success: true,
          message: t('userDeletedWithDataSuccessfully'),
        });
      } catch (error: any) {
        console.error('Error in force delete:', error);
        handleCloseForeignKeyDialog();
        setResultDialog({
          open: true,
          success: false,
          message: getErrorMessage(error, false),
        });
      } finally {
        setIsDeleting(false);
      }
    }
  };

  const getRoleName = (user: User) => {
    // מנסה לקבל את התפקיד בכל הדרכים האפשריות
    if (user.role_id === 1) return t('roleTeacher');
    if (user.role_id === 2) return t('roleStudent');
    if (user.role_id === 3) return t('roleAdmin');
    if (user.role_id === 4) return t('rolePrincipal');

    // מבנה ישן של התפקיד - ניסיון לזהות ולתרגם
    if (user.role?.name) {
      const roleName = user.role.name.toLowerCase();
      if (roleName.includes('מורה') || roleName.includes('teacher')) return t('roleTeacher');
      if (roleName.includes('תלמיד') || roleName.includes('student')) return t('roleStudent');
      if (roleName.includes('מנהל מערכת') || roleName.includes('admin')) return t('roleAdmin');
      if (roleName.includes('מנהל בית ספר') || roleName.includes('principal')) return t('rolePrincipal');
    }

    // מחזיר ערך ברירת מחדל אם לא נמצא
    return t('unknownRole');
  };

  const getRoleChipColor = (roleId: number | undefined) => {
    // קבלת ה-role_id ישירות או מתוך אובייקט role
    const actualRoleId = roleId || (typeof roleId === 'object' ? (roleId as any)?.id : null);

    switch (actualRoleId) {
      case 1:
        return 'primary'; // מורה
      case 2:
        return 'success'; // תלמיד
      case 3:
        return 'error'; // אדמין
      case 4:
        return 'warning'; // מנהל בית ספר
      default:
        return 'default';
    }
  };

  // פונקציה לקבלת כיתות המשתמש לפי תפקיד
  const getUserClasses = (user: User) => {
    if (!user) return [];

    const roleId = user.role_id || user.role?.id;

    if (roleId === 1) {
      // מורה - כיתות שהוא מלמד (Grade.teacher = user.id)
      return grades.filter((grade) => grade.teacher === user.id);
    } else if (roleId === 2) {
      // תלמיד - כיתות מ-grade_ids
      if (user.grade_ids && user.grade_ids.length > 0) {
        return grades.filter((grade) => user.grade_ids!.includes(grade.id));
      }
    }

    return [];
  };

  // פונקציה לקבלת שמות הכיתות של המשתמש לתצוגה
  const getUserClassesDisplay = (user: User) => {
    const userClasses = getUserClasses(user);

    if (userClasses.length === 0) {
      return 'לא נבחרה';
    }

    // בדיקה אם הכיתות מבתי ספר שונים
    const uniqueSchools = new Set(userClasses.map((grade) => grade.school_id));

    if (uniqueSchools.size > 1) {
      // כיתות ממספר בתי ספר - הצג עם שם בית הספר
      return userClasses.map((grade) => `${grade.name} (${grade.School.name})`).join(', ');
    } else {
      // כיתות מאותו בית ספר
      return userClasses.map((grade) => grade.name).join(', ');
    }
  };

  // פונקציה לבדיקה אם המשתמש הנוכחי יכול לערוך/למחוק משתמש אחר
  const canManageUser = (user: User) => {
    // מנהל מערכת יכול לנהל כל משתמש
    if (isAdmin) return true;

    // מנהל בית ספר יכול לנהל רק מורים ותלמידים בבית הספר שלו
    if (isPrincipal) {
      const targetUserRoleId = user.role_id || user.role?.id;

      // לא יכול לנהל מנהלי מערכת או מנהלי בתי ספר אחרים
      if (targetUserRoleId === ROLES.ADMIN || targetUserRoleId === ROLES.PRINCIPAL) {
        return false;
      }

      // יכול לנהל רק משתמשים מבית הספר שלו
      const targetUserSchoolId = user.school?.id;
      if (!targetUserSchoolId || !adminStore.selectedSchoolId) return false;
      return String(targetUserSchoolId) === String(adminStore.selectedSchoolId);
    }

    return false;
  };

  return (
    <div className="usersManagementContainer">
      {/* Header Section */}
      <div className="head-line">
        <h1 className="title">{isPrincipal ? t('schoolUsers') : t('userManagement')} 👥</h1>

        <p className="subtitle">
          {adminStore.selectedSchoolId ? (
            <>
              {isPrincipal ? t('schoolUsersDescriptionFor') : t('userManagementDescriptionFor')}{' '}
              <span style={{ fontWeight: 'bold', color: '#667eea' }}>
                {adminStore.availableSchools.find((school: SchoolActivity) => Number(school.id) === Number(adminStore.selectedSchoolId))?.name ||
                  t('selectedSchool')}
              </span>
            </>
          ) : (
            <>
              {isPrincipal ? t('schoolUsersDescriptionAll') : t('userManagementDescriptionAll')}{' '}
              <span style={{ fontWeight: 'bold', color: '#4caf50' }}>{t('allSchools')}</span>
            </>
          )}
        </p>
      </div>

      <div className="mainCard">
        <div className="mainCardContent">
          <div className="controlsSection">
            <TextField
              className="searchField"
              size="small"
              variant="outlined"
              placeholder={t('searchUsers')}
              value={searchTerm}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
            <div className="actionButtons">
              {/* רק מנהלי מערכת יכולים ליצור בתי ספר חדשים */}
              {isAdmin && (
                <Button
                  className="actionButton"
                  variant="outlined"
                  color="primary"
                  startIcon={<BusinessIcon />}
                  onClick={handleOpenSchoolDialog}
                  size={isMobile ? 'medium' : 'medium'}
                  fullWidth={isMobile}
                >
                  {t('addSchool')}
                </Button>
              )}
              <Button
                className="actionButton"
                variant="outlined"
                color="secondary"
                startIcon={<ClassIcon />}
                onClick={handleOpenGradeDialog}
                size={isMobile ? 'medium' : 'medium'}
                fullWidth={isMobile}
              >
                {t('addClass')}
              </Button>
              <Button
                className="actionButton"
                variant="outlined"
                color="info"
                startIcon={<CloudUploadIcon />}
                onClick={handleOpenImportDialog}
                size={isMobile ? 'medium' : 'medium'}
                fullWidth={isMobile}
              >
                {t('importStudents')}
              </Button>
              <Button
                className="actionButton primary"
                variant="outlined"
                color="success"
                startIcon={<AddIcon />}
                onClick={() => handleOpenDialog()}
                size={isMobile ? 'medium' : 'medium'}
                fullWidth={isMobile}
              >
                {t('addUser')}
              </Button>
            </div>
          </div>

          <div className="contentSection">
            {isLoading ? (
              <div className="loadingContainer">
                <CircularProgress />
              </div>
            ) : isMobile ? (
              // תצוגת כרטיסיות במובייל
              <div className="mobileCardsContainer">
                {filteredUsers.length > 0 ? (
                  filteredUsers.map((user) => (
                    <div key={user.id} className="userMobileCard">
                      <div className="userCardHeader">
                        <div className="userName">{user.name}</div>
                        <div className="userActions">
                          {canManageUser(user) ? (
                            <>
                              <IconButton color="primary" onClick={() => handleOpenDialog(user)} size="small" aria-label={`ערוך את ${user.name}`}>
                                <EditIcon />
                              </IconButton>
                              <IconButton color="error" onClick={() => handleOpenDeleteDialog(user)} size="small" aria-label={`מחק את ${user.name}`}>
                                <DeleteIcon />
                              </IconButton>
                            </>
                          ) : (
                            <span className="noPermissionText">{t('noPermission')}</span>
                          )}
                        </div>
                      </div>
                      <div className="userCardContent">
                        <div className="userEmail">{user.email}</div>
                        <div className="userMetadata">
                          <Chip
                            label={getRoleName(user)}
                            color={getRoleChipColor(user.role_id || user.role?.id)}
                            size="small"
                          />
                          <span className="userEmail">{new Date(user.created_at).toLocaleDateString()}</span>
                        </div>
                        <div className="userDetails">
                          <div className="userDetail">
                            <strong>בית ספר:</strong> {user.school?.name || 'לא נבחר'}
                          </div>
                          <div className="userDetail">
                            <strong>כיתה:</strong> {getUserClassesDisplay(user)}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="emptyStateContainer">
                    <span className="emptyStateText">{searchTerm ? t('noSearchResults') : t('noUsersInSystem')}</span>
                  </div>
                )}
              </div>
            ) : (
              // תצוגת טבלה במסכים גדולים
              <div className="desktopTableContainer">
                <div className="tableWrapper">
                  <table className="usersTable">
                    <thead className="tableHeader">
                      <tr>
                        <th scope="col" className="headerCell nameColumn">
                          <TableSortLabel
                            active={orderBy === 'name'}
                            direction={orderBy === 'name' ? order : 'asc'}
                            onClick={() => handleRequestSort('name')}
                          >
                            {t('name')}
                          </TableSortLabel>
                        </th>
                        <th scope="col" className="headerCell emailColumn">{t('email')}</th>
                        <th scope="col" className="headerCell roleColumn">
                          <TableSortLabel
                            active={orderBy === 'role'}
                            direction={orderBy === 'role' ? order : 'asc'}
                            onClick={() => handleRequestSort('role')}
                          >
                            {t('role')}
                          </TableSortLabel>
                        </th>
                        <th scope="col" className="headerCell schoolColumn">{t('school')}</th>
                        <th scope="col" className="headerCell classColumn">{t('class')}</th>
                        <th scope="col" className="headerCell dateColumn">
                          <TableSortLabel
                            active={orderBy === 'created_at'}
                            direction={orderBy === 'created_at' ? order : 'asc'}
                            onClick={() => handleRequestSort('created_at')}
                          >
                            {t('createdAt')}
                          </TableSortLabel>
                        </th>
                        <th scope="col" className="headerCell actionsColumn">{t('actions')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredUsers.length > 0 ? (
                        filteredUsers.map((user) => (
                          <tr key={user.id} className="tableRow">
                            <td className="tableCell nameCell">{user.name}</td>
                            <td className="tableCell emailCell">{user.email}</td>
                            <td className="tableCell roleCell">
                              <Chip
                                label={getRoleName(user)}
                                color={getRoleChipColor(user.role_id || user.role?.id)}
                                size="small"
                              />
                            </td>
                            <td className="tableCell schoolCell">{user.school?.name || t('noSchool')}</td>
                            <td className="tableCell classCell">
                              {(() => {
                                const userClasses = getUserClasses(user);
                                if (userClasses.length === 0) {
                                  return t('noClass');
                                }

                                const uniqueSchools = new Set(userClasses.map((grade) => grade.school_id));

                                if (uniqueSchools.size > 1) {
                                  // כיתות ממספר בתי ספר
                                  return (
                                    <div className="classDisplayContainer">
                                      {userClasses.map((grade) => (
                                        <span key={grade.id} className="classItem">
                                          {grade.name} ({grade.School.name})
                                        </span>
                                      ))}
                                    </div>
                                  );
                                } else {
                                  // כיתות מאותו בית ספר
                                  return userClasses.map((grade) => grade.name).join(', ');
                                }
                              })()}
                            </td>
                            <td className="tableCell dateCell">{new Date(user.created_at).toLocaleDateString()}</td>
                            <td className="tableCell actionsCell">
                              <div className="actionButtons">
                                {canManageUser(user) && (
                                  <>
                                    <IconButton color="primary" onClick={() => handleOpenDialog(user)} size="small" aria-label={`ערוך את ${user.name}`}>
                                      <EditIcon />
                                    </IconButton>
                                    <IconButton color="error" onClick={() => handleOpenDeleteDialog(user)} size="small" aria-label={`מחק את ${user.name}`}>
                                      <DeleteIcon />
                                    </IconButton>
                                  </>
                                )}
                                {!canManageUser(user) && <span className="noPermissionText">{t('noPermission')}</span>}
                              </div>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr className="emptyTableRow">
                          <td colSpan={7} className="emptyTableCell">
                            {searchTerm ? t('noSearchResults') : t('noUsersInSystem')}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* User Form Modal */}
      <UserFormModal
        isOpen={openDialog}
        onClose={handleCloseDialog}
        onSubmit={handleUserFormSubmit}
        isEditMode={isEditMode}
        initialFormData={formData}
        schools={schools}
        grades={availableGrades}
        isSaving={isSaving}
      />

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={openDeleteDialog}
        onClose={handleCloseDeleteDialog}
        onConfirm={() => handleDeleteUser()}
        type="user"
        itemName={userToDelete?.name || ''}
        isLoading={isDeleting}
      />

      <MessageModal
        isOpen={resultDialog.open}
        onClose={handleCloseResultDialog}
        title={resultDialog.success ? t('success') : t('error')}
        message={resultDialog.message}
        type={resultDialog.success ? 'success' : 'error'}
      />

      <ForeignKeyErrorModal
        isOpen={openForeignKeyDialog}
        onClose={handleCloseForeignKeyDialog}
        onForceDelete={() => {
          handleConfirmForceDelete();
        }}
        error={foreignKeyError}
        isDeleting={isDeleting}
      />

      {/* School Form Modal */}
      <SchoolFormModal
        isOpen={openSchoolDialog}
        onClose={handleCloseSchoolDialog}
        onSubmit={handleSchoolFormSubmit}
        formData={schoolFormData}
        onFormChange={handleSchoolFormChange}
        isSaving={isSavingSchool}
      />

      {/* Grade Form Modal */}
      <GradeFormModal
        isOpen={openGradeDialog}
        onClose={handleCloseGradeDialog}
        onSubmit={handleGradeFormSubmit}
        formData={gradeFormData}
        onFormChange={handleGradeFormChange}
        schools={schools}
        teachers={teachers}
        assignedTeacherIds={grades.filter(g => g.teacher).map(g => g.teacher as number)}
        isSaving={isSavingGrade}
      />

      {/* Prompt: add students after class creation */}
      <Dialog
        open={showAddStudentsPrompt}
        onClose={() => {
          setShowAddStudentsPrompt(false);
          setNewlyCreatedGrade(null);
          setResultDialog({ open: true, success: true, message: t('classCreatedSuccessfully') });
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ textAlign: 'center', pt: 3 }}>
          {t('classCreatedAddStudents')}
        </DialogTitle>
        <DialogActions sx={{ justifyContent: 'center', pb: 3, gap: 1 }} style={{ marginBottom: 0 }}>
          <Button
            variant="outlined"
            onClick={() => {
              setShowAddStudentsPrompt(false);
              setNewlyCreatedGrade(null);
              setResultDialog({ open: true, success: true, message: t('classCreatedSuccessfully') });
            }}
          >
            {t('later')}
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              setShowAddStudentsPrompt(false);
              setShowImportExcelForNewGrade(true);
            }}
          >
            {t('addStudentsNow')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Import Excel for newly created grade */}
      <ImportExcelModal
        isModalOpen={showImportExcelForNewGrade}
        setIsModalOpen={(open) => {
          setShowImportExcelForNewGrade(open);
          if (!open) setNewlyCreatedGrade(null);
        }}
        handleImportStudents={handleImportToNewGrade}
      />

      {/* Failed users from import to new grade */}
      <FailedUsersModal
        isOpen={showImportFailedUsersModal}
        onClose={() => setShowImportFailedUsersModal(false)}
        failedUsers={importFailedUsers}
      />

      <FailedUsersModal
        isOpen={showFailedUsersModal}
        onClose={() => setShowFailedUsersModal(false)}
        failedUsers={failedUsers}
      />

      <ImportStudentsModal
        isOpen={openImportDialog}
        onClose={handleCloseImportDialog}
        onImport={handleSubmitImport}
        selectedFile={selectedFile}
        onFileChange={handleFileChange}
        formData={importFormData}
        onFormChange={handleImportFormChange}
        schools={schools}
        grades={grades.map((grade) => ({ ...grade, teacher: grade.teacher || null }))}
        teachers={teachers}
        isImporting={isImporting}
      />
    </div>
  );
});

export default UsersManagementPage;
